import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Descriptions, Divider, Space, Button, Tag, Badge, Typography, List, Image, Spin, message, Tabs, Tooltip, Row, Col, Table } from 'antd'
import { CheckOutlined, CloseOutlined, ArrowLeftOutlined, UserOutlined, FileProtectOutlined, WalletOutlined, SafetyCertificateOutlined, BankOutlined, GiftOutlined, PauseOutlined, PlayCircleOutlined, EnvironmentOutlined, GlobalOutlined } from '@ant-design/icons'
import type { Merchant, MerchantDocument } from '../types/merchant.types'
import { DocumentImage } from '../../../shared/components/DocumentImage'
import { merchantsService } from '../../../shared/api/services/merchants.service'
import { useAuthStore } from '../../../auth/store/auth-store'
import { WalletCard } from '../components/WalletCard'
import { WalletTransactionsTable } from '../components/WalletTransactionsTable'
import { ActiveUsersDataGrid } from '../components/ActiveUsersDataGrid'
import { formatIST } from '../../../shared/utils/formatters'
import { MerchantInvoiceList } from '../components/MerchantInvoiceList'
import { MerchantReferralHistory } from '../components/MerchantReferralHistory'
import { walletsService } from '../../../shared/api/services/wallets.service'

import type { Wallet } from '../types/wallet.types'

const { Title, Text } = Typography

import {
    getBusinessTypeName,
    getBusinessConstitutionName,
    isCommissionBasedType
} from '../../../shared/constants/businessEnums'
import { paymentMethodsService, type PaymentMethodItem } from '../../../shared/api/services/payment-methods.service'



export const MerchantDetailsScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [merchant, setMerchant] = useState<Merchant | null>(null)
    const [wallet, setWallet] = useState<Wallet | null>(null)
    const [paymentMethodsList, setPaymentMethodsList] = useState<PaymentMethodItem[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [refreshKey, setRefreshKey] = useState(0)
    const fetchedIdRef = useRef<string | null>(null)

    const { hasPermission } = useAuthStore()
    const canVerifyKyc = hasPermission('merchants.verify')

    const fetchData = async (showLoading = true) => {
        if (!id) return
        try {
            if (showLoading) setIsLoading(true)
            const [merchantData, walletData, pmData] = await Promise.all([
                merchantsService.getMerchantById(id),
                walletsService.getWallet(id),
                paymentMethodsService.getPaymentMethods().catch(() => []),
            ])
            setMerchant(merchantData)
            setWallet(walletData)
            setPaymentMethodsList(pmData || [])
        } catch (error: any) {
            message.error(error.message || 'Failed to fetch merchant details')
            navigate('/merchants')
        } finally {
            if (showLoading) setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!id) return
        if (fetchedIdRef.current === id) return
        fetchedIdRef.current = id
        fetchData()
    }, [id])

    const handleViewFile = async (url: string) => {
        try {
            if (!url) return
            message.loading({ content: 'Loading document...', key: 'doc_loading' })
            const downloadUrl = await merchantsService.downloadDocument(url)
            window.open(downloadUrl, '_blank')
            message.success({ content: 'Document loaded', key: 'doc_loading' })
        } catch (error) {
            message.error({ content: 'Failed to open document', key: 'doc_loading' })
        }
    }

    const handleRefreshWallet = async () => {
        if (!id) return
        try {
            const walletData = await walletsService.getWallet(id)
            setWallet(walletData)
            setRefreshKey(prev => prev + 1)
        } catch (error) {
            console.error('Failed to refresh wallet', error)
        }
    }

    const handleVerifyKyc = async () => {
        if (!merchant) return
        try {
            await merchantsService.verifyKyc(merchant.merchant_id)
            message.success('Merchant KYC verified successfully')
            await fetchData(false)
        } catch (error: any) {
            message.error(error.message || 'Failed to verify KYC')
        }
    }

    const handleReject = async () => {
        if (!merchant) return
        try {
            await merchantsService.rejectMerchant(merchant.merchant_id, 'Rejected by admin via details screen')
            message.success('Merchant rejected successfully')
            setMerchant({ ...merchant, status: 'rejected' })
            navigate('/merchants')
        } catch (error: any) {
            message.error(error.message || 'Failed to reject merchant')
        }
    }

    const handleSuspend = async () => {
        if (!merchant) return
        try {
            const updatedMerchant = await merchantsService.suspendMerchant(merchant.merchant_id, 'Suspended by admin via details screen')
            message.success('Merchant suspended successfully')
            setMerchant(updatedMerchant)
        } catch (error: any) {
            message.error(error.message || 'Failed to suspend merchant')
        }
    }

    const handleUnsuspend = async () => {
        if (!merchant) return
        try {
            const updatedMerchant = await merchantsService.unsuspendMerchant(merchant.merchant_id)
            message.success('Merchant unsuspended successfully')
            setMerchant(updatedMerchant)
        } catch (error: any) {
            message.error(error.message || 'Failed to unsuspend merchant')
        }
    }

    const handleUnblock = async () => {
        if (!merchant) return
        try {
            await merchantsService.unblockMerchant(merchant.merchant_id)
            message.success('Merchant unblocked successfully')
            setMerchant({ ...merchant, status: 'active' })
        } catch (error: any) {
            message.error(error.message || 'Failed to unblock merchant')
        }
    }

    const handleVerifyPan = async () => {
        if (!merchant) return
        try {
            await merchantsService.verifyPan(merchant.merchant_id)
            message.success('PAN verified successfully')
            await fetchData(false)
        } catch (error: any) {
            message.error(error.message || 'Failed to verify PAN')
        }
    }

    const handleVerifyGst = async () => {
        if (!merchant) return
        try {
            await merchantsService.verifyGst(merchant.merchant_id)
            message.success('GST verified successfully')
            await fetchData(false)
        } catch (error: any) {
            message.error(error.message || 'Failed to verify GST')
        }
    }

    const handleVerifyBank = async () => {
        if (!merchant) return
        try {
            await merchantsService.verifyBank(merchant.merchant_id)
            message.success('Bank account verified successfully')
            await fetchData(false)
        } catch (error: any) {
            message.error(error.message || 'Failed to verify bank account')
        }
    }

    const isCommissionBased = isCommissionBasedType(merchant?.business_type);

    const isGstRequired = !isCommissionBased && !!merchant?.kyc?.gst_number;
    const isGstVerified = isGstRequired ? !!merchant?.kyc?.gst_verified : true;

    const allDocumentsVerified = !!merchant?.kyc?.pan_verified && isGstVerified && !!merchant?.bank_detail?.is_verified;

    const unverifiedDocs: string[] = [];
    if (!merchant?.kyc?.pan_verified) unverifiedDocs.push('PAN');
    if (isGstRequired && !merchant?.kyc?.gst_verified) unverifiedDocs.push('GST');
    if (!merchant?.bank_detail?.is_verified) unverifiedDocs.push('Bank Account');

    const tooltipMessage = !allDocumentsVerified
        ? `The following documents must be verified first: ${unverifiedDocs.join(', ')}`
        : '';

    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        )
    }

    if (!merchant) {
        return <div>Merchant not found.</div>
    }

    const primaryLocation = merchant.locations?.[0]
    const effectiveLandmark = merchant.landmark || primaryLocation?.landmark || ''
    const effectiveAddress = merchant.address
        || primaryLocation?.address
        || ([effectiveLandmark, primaryLocation?.city, primaryLocation?.state, primaryLocation?.pincode].filter(Boolean).join(', ') || 'N/A')

    const lat = merchant.latitude || primaryLocation?.latitude
    const lng = merchant.longitude || primaryLocation?.longitude
    const mapSearchQuery = lat && lng ? `${lat},${lng}` : (effectiveAddress !== 'N/A' ? effectiveAddress : '')

    const items = [
        {
            key: 'profile',
            label: <span><UserOutlined />Profile & Status</span>,
            children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Descriptions title="Business Information" column={2} bordered>
                        <Descriptions.Item label="Profile Image" span={2}>
                            {merchant.profile_image_url ? (
                                <DocumentImage
                                    documentId={merchant.profile_image_url}
                                    width={100}
                                    height={100}
                                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                                    fallbackUrl="https://via.placeholder.com/100?text=Error"
                                />
                            ) : (
                                <Text type="secondary">No Image</Text>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Business Name">
                            <Text strong>{merchant.business_name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Business Type">{getBusinessTypeName(merchant.business_type)}</Descriptions.Item>
                        <Descriptions.Item label="Constitution">{getBusinessConstitutionName(merchant.business_constitution)}</Descriptions.Item>
                        <Descriptions.Item label="Owner Name">{merchant.owner_name}</Descriptions.Item>
                        <Descriptions.Item label="Email">{merchant.email}</Descriptions.Item>
                        <Descriptions.Item label="Phone">{merchant.mobile}</Descriptions.Item>
                        <Descriptions.Item label="Accepted Payment Methods" span={2}>
                            {(() => {
                                const merchantBitmask = Number(merchant.payment_methods || 0)
                                if (merchantBitmask === 0) {
                                    return <Text type="warning">No Payment Methods Enabled</Text>
                                }

                                const activeMethods = paymentMethodsList.filter(pm => (merchantBitmask & pm.bit_value) !== 0)
                                if (activeMethods.length === 0) {
                                    return <Text type="secondary">Mask: {merchantBitmask}</Text>
                                }

                                return (
                                    <Space size={[4, 8]} wrap>
                                        {activeMethods.map(pm => (
                                            <Tag 
                                                key={pm.id} 
                                                color={pm.status === 'active' ? 'blue' : 'default'}
                                                style={{ padding: '2px 8px', fontSize: 13, fontWeight: 500 }}
                                            >
                                                {pm.name} {pm.status === 'inactive' ? '(Platform Disabled)' : ''}
                                            </Tag>
                                        ))}
                                    </Space>
                                )
                            })()}
                        </Descriptions.Item>
                    </Descriptions>

                    <Card title={<span><EnvironmentOutlined style={{ color: '#1890ff', marginRight: 8 }} />Location & Map</span>} size="small">
                        <Row gutter={[16, 16]}>
                            <Col span={24} md={12}>
                                <Descriptions column={1} bordered size="small">
                                    <Descriptions.Item label="Full Address">{effectiveAddress}</Descriptions.Item>
                                    <Descriptions.Item label="Nearby Landmark">{effectiveLandmark || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="City">{primaryLocation?.city || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="State">{primaryLocation?.state || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="Pincode">{primaryLocation?.pincode || 'N/A'}</Descriptions.Item>
                                    <Descriptions.Item label="GPS Coordinates">
                                        {lat && lng ? (
                                            <Space>
                                                <Text code>{lat}, {lng}</Text>
                                            </Space>
                                        ) : (
                                            <Text type="secondary">No GPS Coordinates</Text>
                                        )}
                                    </Descriptions.Item>
                                </Descriptions>

                                {mapSearchQuery && (
                                    <div style={{ marginTop: 12 }}>
                                        <Button
                                            type="primary"
                                            icon={<GlobalOutlined />}
                                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapSearchQuery)}`, '_blank')}
                                            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#ffffff', fontWeight: 600 }}
                                        >
                                            Open in Google Maps
                                        </Button>
                                    </div>
                                )}
                            </Col>

                            <Col span={24} md={12}>
                                {mapSearchQuery ? (
                                    <iframe
                                        title="Merchant Location Map"
                                        width="100%"
                                        height="220"
                                        frameBorder="0"
                                        style={{ border: '1px solid #d9d9d9', borderRadius: 8 }}
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(mapSearchQuery)}&z=15&output=embed`}
                                    />
                                ) : (
                                    <div style={{ height: 220, border: '1px dashed #d9d9d9', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa' }}>
                                        <EnvironmentOutlined style={{ fontSize: 36, color: '#bfbfbf', marginBottom: 8 }} />
                                        <Text type="secondary">No Location Map Available</Text>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Card>

                    <Descriptions title="Status Information" column={2} bordered>
                        <Descriptions.Item label="Account Status">
                            <Tag color={merchant.status === 'verified' || merchant.status === 'active' ? 'green' : merchant.status === 'pending' ? 'orange' : 'red'}>
                                {merchant.status.toUpperCase()}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="KYC Status">
                            <Badge
                                status={merchant.kyc_status === 'approved' ? 'success' : (merchant.kyc_status === 'rejected' ? 'error' : 'processing')}
                                text={merchant.kyc_status?.toUpperCase() || 'PENDING'}
                            />
                        </Descriptions.Item>
                        {merchant.kyc?.rejection_reason && (
                            <Descriptions.Item label="Rejection Reason" span={2}>
                                <Text type="danger">{merchant.kyc.rejection_reason}</Text>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Created At">{merchant.created_at ? formatIST(merchant.created_at) : 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Updated At">{merchant.updated_at ? formatIST(merchant.updated_at) : 'N/A'}</Descriptions.Item>
                    </Descriptions>

                    <Space>
                        {canVerifyKyc && merchant.kyc_status !== 'approved' && (
                            <Tooltip title={tooltipMessage}>
                                <Button
                                    type="primary"
                                    icon={<CheckOutlined />}
                                    onClick={handleVerifyKyc}
                                    disabled={!allDocumentsVerified}
                                >
                                    Verify KYC
                                </Button>
                            </Tooltip>
                        )}
                        {canVerifyKyc && merchant.status !== 'rejected' && merchant.status !== 'blocked' && merchant.status !== 'suspended' && (
                            <>
                                <Button danger icon={<CloseOutlined />} onClick={handleReject}>Reject</Button>
                                <Button danger icon={<PauseOutlined />} onClick={handleSuspend}>Suspend</Button>
                            </>
                        )}
                        {canVerifyKyc && merchant.status === 'suspended' && (
                            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleUnsuspend}>Unsuspend</Button>
                        )}
                        {canVerifyKyc && merchant.status === 'blocked' && (
                            <Button type="primary" icon={<CheckOutlined />} onClick={handleUnblock}>Unblock</Button>
                        )}
                    </Space>
                </Space>
            )
        },
        {
            key: 'kyc',
            label: <span><FileProtectOutlined />KYC & Documents</span>,
            children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Descriptions title="KYC Details" column={2} bordered>
                        <Descriptions.Item label="PAN Number">
                            <Space>
                                <Text strong>{merchant.kyc?.pan_number || 'N/A'}</Text>
                                {merchant.kyc?.pan_number && (
                                    <Badge status={merchant.kyc?.pan_verified ? 'success' : 'warning'} text={merchant.kyc?.pan_verified ? 'Verified' : 'Unverified'} />
                                )}
                                {canVerifyKyc && merchant.kyc?.pan_number && !merchant.kyc?.pan_verified && (
                                    <Button size="small" type="link" icon={<SafetyCertificateOutlined />} onClick={handleVerifyPan}>
                                        Verify PAN
                                    </Button>
                                )}
                                {merchant.kyc?.pan_verified && (
                                    <Tag color="green" icon={<CheckOutlined />}>PAN Verified</Tag>
                                )}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="GST Number">
                            <Space>
                                <Text strong>{merchant.kyc?.gst_number || (isCommissionBased ? 'N/A' : 'N/A')}</Text>
                                {!merchant.kyc?.gst_number && isCommissionBased && (
                                    <Tag color="blue">Not Required for Commission Agent</Tag>
                                )}
                                {merchant.kyc?.gst_number && (
                                    <Badge status={merchant.kyc?.gst_verified ? 'success' : 'warning'} text={merchant.kyc?.gst_verified ? 'Verified' : 'Unverified'} />
                                )}
                                {canVerifyKyc && merchant.kyc?.gst_number && !merchant.kyc?.gst_verified && (
                                    <Button size="small" type="link" icon={<SafetyCertificateOutlined />} onClick={handleVerifyGst}>
                                        Verify GST
                                    </Button>
                                )}
                                {merchant.kyc?.gst_verified && (
                                    <Tag color="green" icon={<CheckOutlined />}>GST Verified</Tag>
                                )}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions title="Bank Details" column={2} bordered>
                        <Descriptions.Item label="Account Holder Name">{merchant.bank_detail?.account_holder_name || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Bank Name">{merchant.bank_detail?.bank_name || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Account Number">{merchant.bank_detail?.account_number_masked || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="IFSC Code">{merchant.bank_detail?.ifsc_code || 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Account Type">{merchant.bank_detail?.account_type ? merchant.bank_detail.account_type.toUpperCase() : 'N/A'}</Descriptions.Item>
                        <Descriptions.Item label="Verification">
                            <Space>
                                <Badge status={merchant.bank_detail?.is_verified ? 'success' : 'warning'} text={merchant.bank_detail?.is_verified ? 'Verified' : 'Unverified'} />
                                {canVerifyKyc && !merchant.bank_detail?.is_verified && (
                                    <Button size="small" type="link" icon={<BankOutlined />} onClick={handleVerifyBank}>
                                        Verify Bank
                                    </Button>
                                )}
                                {merchant.bank_detail?.is_verified && (
                                    <Tag color="green" icon={<CheckOutlined />}>Bank Verified</Tag>
                                )}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>

                    <Descriptions title="Consents & Legal Audit Trail" column={2} bordered>
                        <Descriptions.Item label="Terms of Service">
                            {(() => {
                                const consent = merchant.consents?.find(c => c.consent_type === 'terms_of_service');
                                const granted = consent ? consent.is_granted : merchant.terms_accepted;
                                return <Tag color={granted ? 'green' : 'red'}>{granted ? 'ACCEPTED' : 'NOT ACCEPTED'}</Tag>;
                            })()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Privacy Policy">
                            {(() => {
                                const consent = merchant.consents?.find(c => c.consent_type === 'privacy_policy');
                                const granted = consent ? consent.is_granted : merchant.terms_accepted;
                                return <Tag color={granted ? 'green' : 'red'}>{granted ? 'ACCEPTED' : 'NOT ACCEPTED'}</Tag>;
                            })()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Data Processing">
                            {(() => {
                                const consent = merchant.consents?.find(c => c.consent_type === 'data_processing');
                                const granted = consent ? consent.is_granted : merchant.terms_accepted;
                                return <Tag color={granted ? 'green' : 'red'}>{granted ? 'CONSENTED' : 'NOT CONSENTED'}</Tag>;
                            })()}
                        </Descriptions.Item>
                        <Descriptions.Item label="Marketing Communications">
                            {(() => {
                                const consent = merchant.consents?.find(c => c.consent_type === 'marketing');
                                const granted = consent ? consent.is_granted : false;
                                return <Tag color={granted ? 'blue' : 'default'}>{granted ? 'OPTED IN' : 'OPTED OUT'}</Tag>;
                            })()}
                        </Descriptions.Item>
                    </Descriptions>

                    {merchant.documents && merchant.documents.length > 0 && (
                        <>
                            <Divider orientation="left">KYC Documents</Divider>
                            <List
                                grid={{ gutter: 16, column: 3 }}
                                dataSource={merchant.documents}
                                renderItem={(item: MerchantDocument) => (
                                    <List.Item>
                                        <Card
                                            hoverable
                                            cover={
                                                <div style={{ height: 200, overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f0f2f5' }}>
                                                    {item.url ? (
                                                        <DocumentImage documentId={item.url} style={{ maxHeight: 200, objectFit: 'contain' }} fallbackUrl="https://via.placeholder.com/200?text=Error" />
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#bfbfbf' }}>
                                                            <FileProtectOutlined style={{ fontSize: 48, marginBottom: 8 }} />
                                                            <Text type="secondary">Not Available</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                            actions={[<Button type="link" onClick={() => handleViewFile(item.url)} disabled={!item.url}>View File</Button>]}
                                        >
                                            <Card.Meta
                                                title={item.name}
                                                description={
                                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                        <Tag color="blue">{item.type.replace(/_/g, ' ').toUpperCase()}</Tag>
                                                        <Badge status={item.status === 'verified' ? 'success' : 'warning'} text={item.status} />
                                                    </Space>
                                                }
                                            />
                                        </Card>
                                    </List.Item>
                                )}
                            />
                        </>
                    )}
                </Space>
            )
        },
        {
            key: 'wallet',
            label: <span><WalletOutlined />Wallet & Billing</span>,
            children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {wallet && <WalletCard wallet={wallet} onUpdate={handleRefreshWallet} />}

                    <Divider orientation="left">Transaction History</Divider>
                    <WalletTransactionsTable merchantId={id!} refreshKey={refreshKey} />

                    <Divider orientation="left">Monthly Invoices</Divider>
                    <MerchantInvoiceList merchantId={id!} />
                </Space>
            )
        },
        {
            key: 'team',
            label: <span><UserOutlined />Team</span>,
            children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={5}>Active Users & Roles</Title>
                    <ActiveUsersDataGrid merchantId={id!} />
                </Space>
            )
        },
        {
            key: 'referrals',
            label: <span><GiftOutlined />Referrals</span>,
            children: (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Title level={5}>Referral History</Title>
                    <MerchantReferralHistory merchantId={parseInt(id!)} />
                </Space>
            )
        }
    ]


    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/merchants')}>
                    Back to Merchants
                </Button>
                <Title level={4} style={{ margin: 0 }}>Merchant: {merchant.business_name}</Title>
            </div>

            <Card>
                <Tabs defaultActiveKey="profile" items={items} />
            </Card>
        </div>
    )
}
